import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import get from 'lodash/get';
import { Link } from 'react-router-dom';
import { intlShape } from 'react-intl';

import MediaCard from '@redbubble/design-system/react/MediaCard';

import { UniqueWithinTypeDecode } from '../../apollo';
import { analyticsPayload } from '../../lib/analytics';
import Favorite from '../Favorite';
import { workLocation } from '../../lib/routing/routing';
import cachedPlaceholderSrc from '../../lib/cachedPlaceholderImage';
import {
  AnalyticsItemsList,
  RbGtmClient,
  parseWorkToAnalyticsItem
} from '../../lib/gtm';
import messages from './messages';

const Artwork = ({
  work,
  analyticsLabel,
  index,
  logEvent,
  intl,
  addWorkToFavorites,
  removeWorkFromFavorites,
  isLoggedIn,
  openSignUpModal,
  loadImage,
  locale,
  logImpression,
  onFavorite
}) => {
  const workId = UniqueWithinTypeDecode(work.id);

  const ref = useRef();
  const observer = useRef();
  const logged = useRef(false);

  useEffect(() => {
    const accumulateImpression = entries => {
      const entry = entries[0];

      if (entry.isIntersecting) {
        const rank = index + 1;
        logImpression(work, rank);
        logged.current = true;
        observer.current.disconnect();
      }
    };

    if (typeof IntersectionObserver !== 'undefined' && !logged.current) {
      observer.current = new IntersectionObserver(accumulateImpression);
      observer.current.observe(ref.current);
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [work, index, logImpression]);

  const workLinkLocation = workLocation({ workId, locale, modal: true });
  const rank = index + 1;
  const onWorkClick = () =>
    logEvent({
      analytics: analyticsPayload.clickThrough(analyticsLabel, workId, rank)
    });
  const { width, height } = get(work, 'image', {});
  const imageUrl = get(work, 'image.url');

  const handleNonMemberFavorite = () => openSignUpModal(imageUrl);

  const favorited = get(work, 'isFavorited', false);

  const toggleFavorite = () => {
    onFavorite(work, !favorited, rank);

    if (favorited) {
      removeWorkFromFavorites(workId);
    } else {
      addWorkToFavorites(workId);

      try {
        RbGtmClient.track({
          event: 'add_to_wishlist',
          ecommerce: {
            items: [
              parseWorkToAnalyticsItem({
                work,
                index,
                analyticsComponentID: AnalyticsItemsList.ExploreFollowFeed
              })
            ],
            currency: null,
            value: null
          }
        });
      } catch (error) {
        console.error('There was error reporting add_to_wishlist event', error);
      }
    }
  };

  const handleFavoriteClick = isLoggedIn
    ? toggleFavorite
    : handleNonMemberFavorite;

  const loadOnVisible = { enabled: true };
  const placeholder = cachedPlaceholderSrc(width, height);

  if (placeholder) loadOnVisible.placeholder = placeholder;

  return (
    <div ref={ref}>
      <MediaCard
        element={Link}
        to={workLinkLocation}
        onClick={onWorkClick}
        rel="nofollow"
        title={get(work, 'title')}
        caption={intl.formatMessage(messages.artistAttribution, {
          artistName: get(work, 'artistShopDisplayName')
        })}
        imageSrc={loadImage ? imageUrl : placeholder}
        transparent
        loadOnVisible={loadOnVisible}
        primaryActionSlot={
          <Favorite favorited={favorited} onClick={handleFavoriteClick} />
        }
      />
    </div>
  );
};

Artwork.propTypes = {
  logEvent: PropTypes.func,
  intl: intlShape.isRequired,
  addWorkToFavorites: PropTypes.func,
  removeWorkFromFavorites: PropTypes.func,
  isLoggedIn: PropTypes.bool,
  openSignUpModal: PropTypes.func,
  onFavorite: PropTypes.func
};

Artwork.defaultProps = {
  logEvent: () => {},
  addWorkToFavorites: () => {},
  removeWorkFromFavorites: () => {},
  isLoggedIn: false,
  openSignUpModal: () => {},
  onFavorite: () => {}
};

const WORK_TYPE = 'Work';
const WORK_FRAGMENT_NAME = 'WorkFieldsForArtwork';
const WORK_FRAGMENT = gql`
  fragment ${WORK_FRAGMENT_NAME} on ${WORK_TYPE} {
    id
    title
    artistShopDisplayName
    artist {
      name
    }
    image {
      url
      width
      height
    }
    isFavorited
  }
`;

export const fragments = [
  {
    type: WORK_TYPE,
    name: WORK_FRAGMENT_NAME,
    fragment: WORK_FRAGMENT
  }
];

export default Artwork;
